import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Animations per emotion; image path will be chosen based on `variant` prop
const MASCOT_ANIMATIONS = {
  idle: 'float',
  happy: 'bounce',
  celebration: 'jump',
  thinking: 'float',
  correct: 'bounce',
  wrong: 'wobble',
  sleeping: 'float'
};

const MESSAGES = {
  home: ['Let\'s learn math!', 'Ready for an adventure?', 'Pick a challenge!'],
  quiz: ['You can do it!', 'Think carefully!', 'Almost there!'],
  correct: ['Great job!', 'You\'re amazing!', 'Keep it up!'],
  wrong: ['Try again!', 'You\'ll get it!', 'Keep trying!'],
  celebration: ['Fantastic!', 'You\'re a star!', 'Brilliant work!'],
};

export default function GameMascot({ emotion = 'idle', position = 'bottom-right', variant = 'owl' }) {
  const makeState = (em = emotion, v = variant) => ({
    image: `/images/mascot/${v}-${em}.svg`,
    animation: MASCOT_ANIMATIONS[em] || 'float'
  });

  const [currentMascotState, setCurrentMascotState] = useState(() => makeState(emotion, variant));
  const [imgError, setImgError] = useState(false);
  const [message, setMessage] = useState('');
  const [isJumping, setIsJumping] = useState(false);
  const [isWaving, setIsWaving] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [isFollowing, setIsFollowing] = useState(false);
  const [lastPosition, setLastPosition] = useState(null);

  // Initialize position based on the position prop
  useEffect(() => {
    if (!lastPosition) {
      const positionMap = {
        'bottom-right': { x: window.innerWidth - 150, y: window.innerHeight - 150 },
        'bottom-left': { x: 50, y: window.innerHeight - 150 },
        'top-right': { x: window.innerWidth - 150, y: 50 },
        'top-left': { x: 50, y: 50 }
      };
      setCoords(positionMap[position] || positionMap['bottom-right']);
    }
  }, [position, lastPosition]);

  // Handle mouse movement
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isFollowing) {
        const x = e.clientX;
        const y = e.clientY;
        setCoords({
          x: x - 50, // offset to center the mascot
          y: y - 50
        });
      }
    };

    const handleMouseUp = () => {
      if (isFollowing) {
        setLastPosition(coords);
        setIsFollowing(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isFollowing, coords]);
  const location = useLocation();

  // Handle route changes
  useEffect(() => {
    const path = location.pathname;
    let newEmotion = 'idle';
    
    if (path === '/') {
      showMessage(MESSAGES.home);
      setIsWaving(true);
      setTimeout(() => setIsWaving(false), 2000);
    } else if (path.includes('quiz')) {
      showMessage(MESSAGES.quiz);
      setIsJumping(true);
      setTimeout(() => setIsJumping(false), 1000);
    }
    
    setCurrentMascotState(makeState(newEmotion, variant));
  }, [location]);

  // Show random message from category
  const showMessage = (messageList) => {
    const randomMessage = messageList[Math.floor(Math.random() * messageList.length)];
    setMessage(randomMessage);
    setIsSpeaking(true);
    setTimeout(() => setIsSpeaking(false), 3000);
  };

  // Public methods to trigger animations
  const celebrate = () => {
    setCurrentMascotState(makeState('celebration', variant));
    showMessage(MESSAGES.celebration);
    setIsJumping(true);
    setTimeout(() => {
      setIsJumping(false);
      setCurrentMascotState(makeState('idle', variant));
    }, 2000);
  };

  const react = (isCorrect) => {
    setCurrentMascotState(makeState(isCorrect ? 'correct' : 'wrong', variant));
    showMessage(MESSAGES[isCorrect ? 'correct' : 'wrong']);
    setIsJumping(isCorrect);
    setTimeout(() => {
      setIsJumping(false);
      setCurrentMascotState(makeState('idle', variant));
    }, 2000);
  };

  return (
    <div 
      style={{
        position: 'fixed',
        left: `${coords.x}px`,
        top: `${coords.y}px`,
        transform: 'translate(0, 0)',
      }}
      className={`fixed z-50 select-none
        transition-all duration-300 ease-out
        ${currentMascotState.animation === 'float' ? 'animate-float' : ''}
        ${isJumping ? 'animate-bounce' : ''}
        ${isWaving ? 'animate-wiggle' : ''}
        hover:scale-110
        cursor-pointer`}
      onMouseDown={(e) => {
        e.preventDefault();
        setIsFollowing(true);
        showMessage(['Drag me anywhere!']);
      }}
    >
      <div className="relative">
        {/* Speech bubble */}
        {isSpeaking && (
          <div className="absolute -top-14 left-1/2 -translate-x-1/2 
                         bg-white/90 dark:bg-gray-800/90 px-3 py-1.5 rounded-lg
                         shadow-md min-w-[100px] text-center
                         animate-bounceIn">
            <div className="text-xs font-medium">{message}</div>
            {/* Speech bubble tail */}
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2
                          border-8 border-transparent border-t-white
                          dark:border-t-gray-800"></div>
          </div>
        )}
        
        {/* Mascot (smaller, less emphasis) */}
        <div className="w-16 h-16 relative transform transition-all duration-300
                       filter drop-shadow-md opacity-95">
          {!imgError ? (
            <img
              src={currentMascotState.image}
              alt="Math Mascot"
              className="w-full h-full object-contain"
              onError={() => setImgError(true)}
            />
          ) : (
            // Inline fallback avatar (simple circle face)
            <svg viewBox="0 0 160 160" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <circle cx="80" cy="80" r="70" fill="#6B7280" />
              <circle cx="80" cy="80" r="60" fill="#9CA3AF" />
              <circle cx="60" cy="70" r="6" fill="#111827" />
              <circle cx="100" cy="70" r="6" fill="#111827" />
              <path d="M60 95 Q80 115 100 95" stroke="#111827" strokeWidth="4" fill="none" strokeLinecap="round" />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}